import React, { useEffect, useRef, useState } from 'react';
import { CalendarCheck2, FileText, X } from 'lucide-react';
import Modal from 'components/ui/Modal';
import Button from 'components/ui/Button';
import Select from 'components/ui/Select';
import DatePicker from 'components/ui/DatePicker';
import { hoyCR } from 'lib/fechaCR';
import { fechaCorta } from 'lib/auditoria';
import {
  RESULTADOS, RESULTADO_LABEL, RESULTADO_PILL, TIPOS_EVENTO, eventoVacio, prepararEvento, resultadoObligatorio,
  ofrecerFechaInstalacion,
} from 'lib/cables';
import { validarInformePdf } from 'services/cablesService';
import { Campo, Texto } from 'pages/arboles/Campos';
import { mensajeError } from 'lib/errores';

// Evento de un cable (inspección anual del certificador, revisión,
// mantenimiento, cambio de cable, falla). Se guarda una vez: después no se
// edita, solo un administrador lo anula con motivo.
//   onGuardar(datos, informePdf) devuelve una promesa con el evento guardado.
//   onFechaInstalacion(fecha) actualiza la ficha (segundo paso de un cambio
//   de cable, solo si la persona lo confirma).
export default function FormEvento({ open, onClose, linea, onGuardar, onFechaInstalacion }) {
  const hoy = hoyCR();
  const [form, setForm] = useState(() => eventoVacio(hoy));
  const [informe, setInforme] = useState(null);
  const [errores, setErrores] = useState({});
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  // Segundo paso del cambio de cable: { fecha } del evento ya guardado.
  const [paso2, setPaso2] = useState(null);
  const input = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(eventoVacio(hoy));
    setInforme(null);
    setErrores({});
    setError('');
    setEnviando(false);
    setPaso2(null);
  }, [open, hoy]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const elegirPdf = (e) => {
    const f = e.target.files?.[0] || null;
    e.target.value = '';
    const problema = validarInformePdf(f);
    setErrores((x) => ({ ...x, informe: problema }));
    setInforme(problema ? null : f);
  };

  const guardar = async () => {
    if (enviando) return;
    const { datos, errores: e } = prepararEvento(form, hoy);
    setErrores(e);
    if (Object.keys(e).length) {
      setError('Revise los campos marcados.');
      return;
    }
    setEnviando(true);
    setError('');
    try {
      await onGuardar(datos, informe);
      if (ofrecerFechaInstalacion(datos, linea) && onFechaInstalacion) {
        setEnviando(false);
        setPaso2({ fecha: datos.fecha });
        return;
      }
      onClose();
    } catch (err) {
      setError(mensajeError(err, 'No se pudo guardar el evento.'));
      setEnviando(false);
    }
  };

  const actualizarFecha = async () => {
    if (enviando) return;
    setEnviando(true);
    setError('');
    try {
      await onFechaInstalacion(paso2.fecha);
      onClose();
    } catch (err) {
      setError(mensajeError(err, 'No se pudo actualizar la fecha de instalación.'));
      setEnviando(false);
    }
  };

  if (paso2) {
    return (
      <Modal open={open} onClose={enviando ? () => {} : onClose} title="Cambio de cable registrado" maxWidth="max-w-md" cerrarConFondo={false}
        footer={(
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="lg" className="flex-1 px-4" onClick={onClose} disabled={enviando}>Dejarla igual</Button>
            <Button type="button" size="lg" className="flex-1 px-4" onClick={actualizarFecha} loading={enviando}>Actualizar la ficha</Button>
          </div>
        )}>
        <div className="space-y-3 text-sm text-foreground" data-testid="paso-fecha-instalacion">
          <p className="flex items-start gap-2">
            <CalendarCheck2 size={18} className="mt-0.5 shrink-0 text-success" aria-hidden="true" />
            <span>El evento quedó guardado.</span>
          </p>
          <p>
            ¿Desea actualizar la fecha de instalación de la ficha de {linea?.codigo} al {fechaCorta(paso2.fecha)}?
            {' '}La vida útil restante se calcula desde esa fecha.
          </p>
          <p className="text-xs text-muted-foreground">
            Fecha actual en la ficha: {linea?.fecha_instalacion ? fechaCorta(linea.fecha_instalacion) : 'sin dato'}. El cambio queda en el historial.
          </p>
          {error && <p role="alert" className="rounded-lg bg-error/10 px-2.5 py-2 text-sm text-error">{error}</p>}
        </div>
      </Modal>
    );
  }

  const obligatorio = resultadoObligatorio(form.tipo);

  return (
    <Modal open={open} onClose={enviando ? () => {} : onClose} title={`Nuevo evento${linea?.codigo ? ` · ${linea.codigo}` : ''}`}
      maxWidth="max-w-2xl" cerrarConFondo={false}
      footer={(
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="lg" className="flex-1 px-4" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button type="button" size="lg" className="flex-1 px-4" onClick={guardar} loading={enviando}>Guardar evento</Button>
        </div>
      )}>
      <div className="space-y-4">
        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Evento</legend>
          <Campo label="Fecha" requerido error={errores.fecha}>
            <DatePicker value={form.fecha} onChange={set('fecha')} max={hoy} aria-label="Fecha del evento" className="w-full" />
          </Campo>
          <Campo label="Tipo" requerido error={errores.tipo}>
            <Select value={form.tipo} onChange={set('tipo')} aria-label="Tipo de evento" className="w-full">
              {TIPOS_EVENTO.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </Select>
          </Campo>
          <Campo label="Responsable" requerido error={errores.responsable_nombre}>
            <Texto value={form.responsable_nombre} onChange={set('responsable_nombre')} maxLength={120} />
          </Campo>
          <Campo label="Profesión">
            <Texto value={form.responsable_profesion} onChange={set('responsable_profesion')} placeholder="Ingeniero, técnico certificado…" />
          </Campo>
          <Campo label="Empresa">
            <Texto value={form.empresa} onChange={set('empresa')} />
          </Campo>
          <Campo label="Certificado o acreditación">
            <Texto value={form.certificado} onChange={set('certificado')} placeholder="ACCT, PRCA, número de colegiado…" />
          </Campo>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-foreground">
            Resultado{obligatorio ? <span className="text-error" aria-hidden="true"> *</span> : <span className="font-normal text-muted-foreground"> (opcional)</span>}
          </legend>
          <div role="radiogroup" aria-label="Resultado" className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {RESULTADOS.map((r) => {
              const activo = form.resultado === r;
              return (
                <button key={r} type="button" role="radio" aria-checked={activo}
                  onClick={() => set('resultado')(activo && !obligatorio ? '' : r)}
                  className={`h-11 rounded-lg text-sm font-semibold transition ${
                    activo ? `${RESULTADO_PILL[r]} ring-2 ring-offset-1 ring-foreground/20` : 'border border-input bg-white text-foreground hover:bg-muted'
                  }`}>
                  {RESULTADO_LABEL[r]}
                </button>
              );
            })}
          </div>
          {errores.resultado
            ? <p className="mt-1 text-xs text-error">{errores.resultado}</p>
            : !obligatorio && <p className="mt-1 text-xs text-muted-foreground">Solo la inspección anual exige resultado. Tocar de nuevo lo quita.</p>}
        </fieldset>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo label="Detalle" className="sm:col-span-2" ayuda="Qué se revisó o qué se hizo">
            <Texto area rows={3} value={form.detalle} onChange={set('detalle')} />
          </Campo>
          <Campo label="Recomendaciones"><Texto area rows={3} value={form.recomendaciones} onChange={set('recomendaciones')} /></Campo>
          <Campo label="Acciones" ayuda="Ajuste de tensión, cambio de grapas, lubricación…">
            <Texto area rows={3} value={form.acciones} onChange={set('acciones')} />
          </Campo>
          <Campo label="Próxima revisión" error={errores.proxima_fecha}>
            <DatePicker value={form.proxima_fecha} onChange={set('proxima_fecha')} min={form.fecha || undefined}
              clearable aria-label="Próxima revisión" className="w-full" />
          </Campo>
          <div className="flex flex-col gap-1 text-sm font-medium text-foreground">
            <span>Informe (PDF)</span>
            {informe ? (
              <div className="flex h-10 items-center gap-2 rounded-lg border border-border bg-muted/40 px-2.5 text-sm">
                <FileText size={16} className="shrink-0 text-primary" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{informe.name}</span>
                <button type="button" onClick={() => setInforme(null)} aria-label="Quitar informe"
                  className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                  <X size={16} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => input.current?.click()} className="justify-start">
                <FileText size={16} className="mr-1.5" aria-hidden="true" />Adjuntar PDF
              </Button>
            )}
            <input ref={input} type="file" accept="application/pdf,.pdf" className="sr-only" aria-label="Informe PDF" onChange={elegirPdf} />
            {errores.informe && <span className="text-xs font-normal text-error">{errores.informe}</span>}
          </div>
        </div>
        {form.tipo === 'cambio_cable' && (
          <p className="rounded-lg bg-muted px-3 py-2 text-xs text-foreground">
            Al guardar, se le preguntará si actualiza la fecha de instalación de la ficha con la fecha de este cambio.
          </p>
        )}
        <p className="text-xs text-muted-foreground">Después de guardado, el evento no se edita: si tiene un error, un administrador lo anula con motivo y se registra otro.</p>
        {error && <p role="alert" className="rounded-lg bg-error/10 px-2.5 py-2 text-sm text-error">{error}</p>}
      </div>
    </Modal>
  );
}
